"use client";

import React, { useEffect, useState, useRef } from "react";
import { ChartBar, Zap, TrendingUp, Target, Award } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function useCountUp(end: number, duration: number = 2000, start: boolean = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    window.requestAnimationFrame(step);
  }, [end, duration, start]);
  return count;
}
export default function About() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const stats = [{
    label: "Businesses Empowered",
    value: 150,
    suffix: "+"
  }, {
    label: "Efficiency Improvements",
    value: 40,
    suffix: "%"
  }];
  return <section id="about" ref={ref} className="py-24 bg-background relative overflow-hidden">
      {}
      <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-[var(--chart-1)]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-80 h-80 bg-[var(--primary)]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        
        {}
        <div className={`flex justify-start mb-8 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Badge variant="default" className="bg-[var(--chart-1)] hover:bg-[var(--chart-1)]/90 text-white border-none px-4 py-1.5 text-xs tracking-widest font-semibold uppercase shadow-lg shadow-[var(--chart-1)]/20">
            About Us
          </Badge>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {}
          <div className={`lg:col-span-5 flex flex-col gap-6 transition-all duration-700 delay-200 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-[1.1]">
              Accelerating <span className="text-[var(--chart-1)]">Growth</span> for Modern Businesses
            </h2>
            
            <div className="space-y-6 text-lg text-muted-foreground font-serif leading-relaxed">
              <p>
                Profissa Pro™ is a financial intelligence platform designed to help entrepreneurs and project‑based businesses gain full visibility into their financial operations. The platform transforms complex financial data into structured insights that support smarter decision‑making and sustainable growth. Profissa Pro™ allows business owners to clearly separate project funds from operational company funds, track real profitability, monitor expenses, and manage financial performance with precision. Inspired by professional project management principles, the platform integrates financial control, project tracking, and performance analysis in a single environment. Profissa Pro™ empowers entrepreneurs to understand their numbers, improve financial discipline, and grow their businesses with clarity and confidence.
              </p>
            </div>

            {}
          </div>

          {}
          <div className={`lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4 transition-all duration-700 delay-400 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            
            {}
            <div className="md:col-span-2 flex flex-wrap gap-4 items-center">
              <Button size="lg" className="bg-[var(--chart-1)] hover:bg-[var(--chart-1)]/90 text-white font-semibold px-8 py-6 h-auto rounded-lg shadow-lg shadow-[var(--chart-1)]/20 transition-all hover:scale-[1.02] active:scale-95">
                Start Exploring the Platform
              </Button>
              <Button size="lg" variant="outline" className="font-semibold px-8 py-6 h-auto rounded-lg border-border/50 hover:bg-secondary transition-all hover:scale-[1.02] active:scale-95">
                Watch the Demo
              </Button>
              <Button size="lg" variant="secondary" className="font-semibold px-8 py-6 h-auto rounded-lg bg-secondary/80 hover:bg-secondary transition-all hover:scale-[1.02] active:scale-95">
                Explore Profissa Pro™
              </Button>
            </div>

            {}
            <div className="md:col-span-1">
              <Card className="h-full bg-card/50 backdrop-blur-sm border-border/50 hover:border-[var(--chart-1)]/50 transition-all duration-500 hover:shadow-xl hover:shadow-[var(--chart-1)]/10 group overflow-hidden">
                <CardHeader>
                  <div className="w-fit p-3 mb-4 rounded-xl bg-[var(--chart-2)]/10 text-[var(--chart-2)] group-hover:scale-110 transition-transform duration-500">
                    <ChartBar className="w-6 h-6" />
                  </div>
                  <CardTitle className="text-lg">Smart Profit Calculation</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="group-hover:text-foreground transition-colors duration-300">
                    Calculate the real profit of every project by combining price, cost, time, and operational expenses.
                  </CardDescription>
                </CardContent>
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-[var(--chart-2)] to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </Card>
            </div>

            {}
            <div className="md:col-span-1">
              <Card className="h-full bg-card/50 backdrop-blur-sm border-border/50 hover:border-[var(--chart-1)]/50 transition-all duration-500 hover:shadow-xl hover:shadow-[var(--chart-1)]/10 group overflow-hidden">
                <CardHeader>
                  <div className="w-fit p-3 mb-4 rounded-xl bg-[var(--chart-4)]/10 text-[var(--chart-4)] group-hover:scale-110 transition-transform duration-500">
                    <Target className="w-6 h-6" />
                  </div>
                  <CardTitle className="text-lg">Project Financial Control</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="group-hover:text-foreground transition-colors duration-300">
                    Track financial performance across projects and understand which work actually generates profit.
                  </CardDescription>
                </CardContent>
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-[var(--chart-4)] to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </Card>
            </div>

            {}
            <div className="md:col-span-1">
              <Card className="h-full bg-card/50 backdrop-blur-sm border-border/50 hover:border-[var(--chart-1)]/50 transition-all duration-500 hover:shadow-xl hover:shadow-[var(--chart-1)]/10 group overflow-hidden">
                <CardHeader>
                  <div className="w-fit p-3 mb-4 rounded-xl bg-[var(--chart-1)]/10 text-[var(--chart-1)] group-hover:scale-110 transition-transform duration-500">
                    <Zap className="w-6 h-6" />
                  </div>
                  <CardTitle className="text-lg">Cash Flow Intelligence</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="group-hover:text-foreground transition-colors duration-300">
                    Monitor inflows, outflows, and financial alerts in a simple operational dashboard.
                  </CardDescription>
                </CardContent>
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-[var(--chart-1)] to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </Card>
            </div>

          </div>
        </div>

        {}
        <div className={`mt-20 pt-10 border-t border-border/60 transition-all duration-700 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1">
              <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                <Award className="w-4 h-4 text-[var(--chart-1)]" />
                Proven Results
              </h3>
            </div>
            
            {stats.map((stat, i) => <div key={i} data-index={i} className="flex flex-col lg:border-l lg:border-border/60 lg:pl-8">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl md:text-5xl font-mono font-bold text-foreground tracking-tighter">
                    <Counter value={stat.value} start={isVisible} />
                  </span>
                  <span className="text-2xl md:text-3xl font-mono text-[var(--chart-1)]">{stat.suffix}</span>
                </div>
                <p className="text-sm font-medium text-muted-foreground mt-1 uppercase tracking-wide">
                  {stat.label}
                </p>
              </div>)}

          </div>
        </div>

      </div>
    </section>;
}
function Counter({
  value,
  start
}: {
  value: number;
  start: boolean;
}) {
  const count = useCountUp(value, 2500, start);
  return <>{count}</>;
}